import { faEdit } from "@fortawesome/pro-regular-svg-icons";
import { observer } from "mobx-react-lite";

import { Button } from "src/design-system/Button";
import { useModal } from "src/design-system/Modal";
import { FlowVersionControlModals } from "src/flow/FlowVersionControlModals";
import { useFlowVersionModals } from "src/flow/useFlowVersionModals";
import { ContinueEditingVersionModal } from "src/flowContainer/ContinueEditingVersionButton/ContinueEditingVersionModal";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { useMarkAllSimulationsAsDeleted } from "src/simulations/api/mutations";
import { useSimulationJob } from "src/simulations/useSimulationJob";
import { useSimulationRuns } from "src/simulations/useSimulationRun";

export const ContinueEditingVersionButton: React.FC = observer(() => {
  const {
    isOpen: isContinueEditingVersionModalOpen,
    openModal: openContinueEditingVersionModal,
    closeModal: closeContinueEditingVersionModal,
  } = useModal<{
    flowVersionName: string;
  }>();

  const { flow, workspace, version: flowVersion } = useAuthoringContext();

  const {
    currentModal,
    buildOpenModalAction,
    onClose,
    onAfterLeave,
    selectedVersion,
    unpublishedTemplates,
  } = useFlowVersionModals();
  const { simulationJob } = useSimulationJob(
    flow?.id,
    workspace.base_url ?? "",
  );
  const { simulationRuns } = useSimulationRuns(flow?.id, flowVersion.id);

  const markAllSimulationAsDeletedMutation = useMarkAllSimulationsAsDeleted(
    workspace.base_url!,
    simulationJob?.id || "",
  );

  const onDiscardSimulations = async () => {
    await markAllSimulationAsDeletedMutation.mutateAsync(
      simulationRuns?.map((run) => run.id) ?? [],
    );
    closeContinueEditingVersionModal();
  };

  const openDuplicateDatasetModal = buildOpenModalAction(
    "add_version",
    flowVersion,
  );

  const onDuplicateDataset = () => {
    closeContinueEditingVersionModal();
    openDuplicateDatasetModal();
  };

  return (
    <>
      <Button
        iconLeft={faEdit}
        variant="secondary"
        onClick={() =>
          openContinueEditingVersionModal({ flowVersionName: flowVersion.name })
        }
      >
        Continue editing
      </Button>
      <ContinueEditingVersionModal
        closeModal={closeContinueEditingVersionModal}
        discardingSimulationsPending={
          markAllSimulationAsDeletedMutation.isPending
        }
        flowVersionName={flowVersion.name}
        isOpen={isContinueEditingVersionModalOpen}
        onDiscardSimulations={onDiscardSimulations}
        onDuplicateVersion={onDuplicateDataset}
      />
      <FlowVersionControlModals
        afterLeave={onAfterLeave}
        flow={flow}
        modal={currentModal}
        unpublishedTemplates={unpublishedTemplates}
        version={selectedVersion}
        onClose={onClose}
      />
    </>
  );
});
